import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedApi, scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse, EndpointResponse } from '@/types/endpoints';
import type { GenericCategory, GenericContainersResponse, GenericMapping, GenericTemplateRecord } from '@/types/genericTemplate';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from '../requestOptions';

const GENERIC_BASE = 'astur/integrations/generic';

export type GenericTemplateRequestOptions = ReadRequestOptions;

type GenericTemplateListEnvelope = { data?: GenericTemplateRecord[] };
type GenericTypeSearchEnvelope = { data?: Array<{ type_name?: string | null }> };

const organizationStore = (scope: RequestScope): string => {
  if (!scope || scope.kind !== 'organization') {
    throw new Error('Generic-template requests require an explicit organization scope');
  }
  return scope.organization;
};

export async function getGenericTemplate(
  scope: RequestScope,
  args: { genericTemplateId: number | string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GenericTemplateRecord>> {
  try {
    const res = await scopedChannelApi(scope).get<GenericTemplateRecord>(`${GENERIC_BASE}/templates/${args.genericTemplateId}`, {
      ...options,
      headers: buildAsturHeaders(),
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function getGenericTemplates(
  scope: RequestScope,
  args: { subscriptionId: number },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GenericTemplateRecord[]>> {
  try {
    const res = await scopedChannelApi(scope).get<GenericTemplateListEnvelope>(`${GENERIC_BASE}/templates`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { query: '{}' },
    });
    return { data: Array.isArray(res.data?.data) ? res.data.data : [], status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function getGenericTemplateTypes(
  scope: RequestScope,
  args: { subscriptionId: number },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<string[]>> {
  try {
    const res = await scopedChannelApi(scope).get<GenericTypeSearchEnvelope>(`${GENERIC_BASE}/types/search`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: {
        store: organizationStore(scope),
        query: JSON.stringify({ id: { $orderby: 'desc' } }),
      },
    });
    const types = Array.isArray(res.data?.data)
      ? res.data.data.flatMap((entry) => (typeof entry?.type_name === 'string' && entry.type_name ? [entry.type_name] : []))
      : [];
    return { data: types, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export interface CreateGenericTemplateBody {
  channel_name: string;
  system_version: number;
  generic_template_name: string;
  generic_template_type: string;
  generic_template_json: '{}';
}

export async function createGenericTemplate(
  scope: RequestScope,
  args: { subscriptionId: number; body: CreateGenericTemplateBody },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<GenericTemplateRecord>> {
  try {
    const res = await scopedChannelApi(scope).post<GenericTemplateRecord>(`${GENERIC_BASE}/templates`, args.body, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function resolveSubscriptionIdForIntegration(
  scope: RequestScope,
  integrationId: number,
  options: ReadRequestOptions
): Promise<number | null> {
  try {
    const response = await scopedApi(scope).get<EndpointResponse<Array<{ id: number; integration_id: number }>>>('/integration_subscription', {
      ...options,
      params: { query: JSON.stringify({ integration_id: integrationId }) },
    });
    const result = response.data;
    if (result.isError || !Array.isArray(result.result)) return null;
    const match = result.result.find((subscription) => subscription.integration_id === integrationId) ?? result.result[0];
    return match?.id ?? null;
  } catch {
    return null;
  }
}

export async function getGenericContainers(
  scope: RequestScope,
  args: { subscriptionId: number; typeName: string; channelVersion: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GenericContainersResponse>> {
  try {
    const res = await scopedChannelApi(scope).get<GenericContainersResponse>(`${GENERIC_BASE}/attributes/containers`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { type_name: args.typeName, channel_version: args.channelVersion || '1.0' },
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function getGenericCategory(
  scope: RequestScope,
  args: { subscriptionId: number; typeName: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GenericCategory | null>> {
  try {
    const res = await scopedChannelApi(scope).get<{ data?: GenericCategory }>(`${GENERIC_BASE}/types/search`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { query: JSON.stringify({ type_name: args.typeName }) },
    });
    return { data: res.data?.data ?? null, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export interface SaveGenericTemplateBody {
  generic_template_name: string;
  channel_version?: string;
  enabled?: boolean;
  generic_template_json: '{}';
  mappings: string;
}

export async function saveGenericTemplate(
  scope: RequestScope,
  args: { genericTemplateId: number | string; subscriptionId: number; body: SaveGenericTemplateBody },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<GenericTemplateRecord>> {
  try {
    const res = await scopedChannelApi(scope).put<GenericTemplateRecord>(`${GENERIC_BASE}/templates/${args.genericTemplateId}`, args.body, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { store: organizationStore(scope) },
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export async function deleteGenericTemplate(
  scope: RequestScope,
  args: { genericTemplateId: number | string; subscriptionId: number },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<{ success?: boolean }>> {
  try {
    const res = await scopedChannelApi(scope).delete<{ success?: boolean }>(`${GENERIC_BASE}/templates/${args.genericTemplateId}`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { store: organizationStore(scope) },
    });
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}

export type { GenericMapping };
